import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useContext} from "react";

import {GuideAccessGate} from "@khan/guide-core";
import {useGuideState} from "@khan/guide-state";

import useMediaQuery from "#coach-dashboard-package/khanmigo/hooks/use-media-query.ts";

import PageTemplateContext from "../../page-template-context.ts";
import BasePageTemplate, {
    type BasePageTemplateProps,
} from "../base-page-template.tsx";

/**
 * A customizable component that can be used to render a page template,
 * including the site header, site footer, and shared styles and utilties.
 *
 * You probably don't want to use this component directly; instead, use
 * DefaultPageTemplate for the default experience.
 */
const BaseWithKhanmigoPageTemplate = ({
    children,
    sidebar,
    ...restProps
}: BasePageTemplateProps): React.ReactElement => {
    const isSmallScreen = useMediaQuery("(max-width: 719px)");
    const guideOpen = useGuideState("open");
    const shouldHideSiteHeader = isSmallScreen && guideOpen;
    const {hasPageTemplate} = useContext(PageTemplateContext);
    if (hasPageTemplate) {
        // If there's already a template ancestor, then just render children
        return <>{children}</>;
    }

    // No sidebar?  Return the usual layout.
    if (!sidebar) {
        return <BasePageTemplate {...restProps}>{children}</BasePageTemplate>;
    }

    // If we're providing a sidebar, then we need a slightly different layout.
    // This is to enable the designs where the header spans the entire page,
    // with the main content/footer as one column, and the sidebar as another.
    return (
        <BasePageTemplate
            {...restProps}
            sidebar={<GuideAccessGate>{sidebar}</GuideAccessGate>}
            showHeader={!shouldHideSiteHeader}
            innerContainerStyle={styles.innerContainer}
            mainContentWrapperStyle={styles.mainContentWrapper}
        >
            {children}
        </BasePageTemplate>
    );
};

export default BaseWithKhanmigoPageTemplate;

const styles = StyleSheet.create({
    innerContainer: {
        display: "grid",
        gridTemplateColumns: "1fr auto",
        height: "100%",
        overflow: "hidden",
    },
    mainContentWrapper: {
        overflowY: "scroll",
        overflowX: "hidden",
    },
});
